/**
 * useVerdicts Hook
 * 
 * React hook for managing verdict data with loading states and caching.
 * 
 * AI analysis now via triggerAnalysisAtSave → verdict.forensicRoast
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts';
import {
  createVerdict,
  getVerdicts,
  getVerdict,
  updateVerdict,
  deleteVerdict,
  markAsSold,
  unmarkSold,
} from '@/services/verdicts';
import {
  Verdict,
  VerdictInputs,
  VerdictCalculations,
  VerdictFlags,
  toVerdictSummary,
  VerdictSummary
} from '@/types';

interface UseVerdictsReturn {
  // Data
  verdicts: Verdict[];
  summaries: VerdictSummary[];
  
  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  
  // Actions
  saveVerdict: (
    inputs: VerdictInputs,
    calculations: VerdictCalculations,
    flags: VerdictFlags,
  ) => Promise<Verdict | null>;
  updateVerdict: (verdictId: string, updates: Partial<Verdict>) => Promise<void>;
  loadVerdicts: () => Promise<void>;
  removeVerdict: (verdictId: string) => Promise<void>;
  markVerdictSold: (verdictId: string, soldPrice: number, soldNote?: string, soldDate?: string, soldBuyer?: string, reconciliationStatus?: 'reported' | 'confirmed' | 'confirmed_manual', linkedTransactionId?: string | null) => Promise<void>;
  unmarkVerdictSold: (verdictId: string) => Promise<void>;

  // Helpers
  getVerdictById: (verdictId: string) => Verdict | undefined;
}

interface UseVerdictsOptions {
  // Set to false on pages that SET the refresh signal (ResultPage, VerdictDetailPage)
  // to prevent them from consuming their own signal before Canvas pages can see it
  watchForRefresh?: boolean;
}

export function useVerdicts(options: UseVerdictsOptions = {}): UseVerdictsReturn {
  const { watchForRefresh = true } = options;
  
  const { user, isAuthenticated, profile, updateProfile, waitForUser } = useAuth();
  const [verdicts, setVerdicts] = useState<Verdict[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track which data version this instance has seen
  const [dataVersion, setDataVersion] = useState(0);
  
  // Load verdicts when user changes
  const loadVerdicts = useCallback(async () => {
    if (!user) {
      setVerdicts([]);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getVerdicts(user.uid);
      // Filter out soft-deleted verdicts (client-side to avoid Firestore composite indexes)
      setVerdicts(data.filter(v => !v.deletedAt));
    } catch (err: any) {
      console.error('Error loading verdicts:', err);
      setError(err.message || 'Failed to load verdicts');
    } finally {
      setIsLoading(false);
    }
  }, [user]);
  
  // Auto-load on mount and user change
  useEffect(() => {
    if (isAuthenticated) {
      loadVerdicts();
    } else {
      setVerdicts([]);
    }
  }, [isAuthenticated, loadVerdicts]);
  
  // Check for data changes signaled by other components
  // Uses a version number so multiple hook instances can all react
  // Only enabled on pages that RECEIVE updates (Dashboard, Portfolio), not pages that SEND them
  useEffect(() => {
    if (!watchForRefresh) return;
    
    const checkForUpdates = () => {
      const signalVersion = sessionStorage.getItem('verdictsVersion');
      const version = signalVersion ? parseInt(signalVersion, 10) : 0;
      
      if (version > dataVersion) {
        setDataVersion(version);
        if (isAuthenticated) {
          loadVerdicts();
        }
      }
    };
    
    // Check immediately
    checkForUpdates();
    
    // Check periodically
    const interval = setInterval(checkForUpdates, 300);
    
    return () => clearInterval(interval);
  }, [watchForRefresh, isAuthenticated, loadVerdicts, dataVersion]);
  
  // Save a new verdict
  const saveVerdict = useCallback(async (
    inputs: VerdictInputs,
    calculations: VerdictCalculations,
    flags: VerdictFlags,
  ): Promise<Verdict | null> => {
    // If user is null (optimistic auth), wait for real auth state before giving up.
    let resolvedUser = user;
    if (!resolvedUser) {
      console.warn('[useVerdicts] user null at save time — waiting for auth…');
      resolvedUser = await waitForUser();
    }
    if (!resolvedUser) {
      console.error('[useVerdicts] No user after auth resolved — cannot save');
      setError('Must be logged in to save');
      return null;
    }

    setIsSaving(true);
    setError(null);

    try {
      const newVerdict = await createVerdict(resolvedUser.uid, inputs, calculations, flags);
      console.log('[useVerdicts] Verdict created:', newVerdict.id);

      // Optimistically update local state
      setVerdicts(prev => [newVerdict, ...prev]);

      return newVerdict;
    } catch (err: any) {
      console.error('[useVerdicts] Error saving verdict:', err);
      setError(err.message || 'Failed to save verdict');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [user, waitForUser]);
  
  // Delete a verdict
  const removeVerdict = useCallback(async (verdictId: string) => {
    if (!user) return;
    
    try {
      await deleteVerdict(user.uid, verdictId);
      
      // Optimistically update local state
      setVerdicts(prev => prev.filter(v => v.id !== verdictId));

      // Bump version so other hook instances (e.g. Dashboard sparkline) refetch
      const currentVersion = parseInt(sessionStorage.getItem('verdictsVersion') || '0', 10);
      sessionStorage.setItem('verdictsVersion', String(currentVersion + 1));
    } catch (err: any) {
      console.error('Error deleting verdict:', err);
      setError(err.message || 'Failed to delete verdict');
      throw err;
    }
  }, [user]);
  
  // Update a verdict
  const updateVerdictCallback = useCallback(async (verdictId: string, updates: Partial<Verdict>) => {
    if (!user) return;

    try {
      await updateVerdict(user.uid, verdictId, updates);

      // Optimistically update local state
      setVerdicts(prev => prev.map(v =>
        v.id === verdictId
          ? { ...v, ...updates, updatedAt: new Date().toISOString() }
          : v
      ));
    } catch (err: any) {
      console.error('Error updating verdict:', err);
      setError(err.message || 'Failed to update verdict');
      throw err;
    }
  }, [user]);

  // Mark verdict as sold
  const markVerdictSold = useCallback(async (verdictId: string, soldPrice: number, soldNote?: string, soldDate?: string, soldBuyer?: string, reconciliationStatus?: 'reported' | 'confirmed' | 'confirmed_manual', linkedTransactionId?: string | null) => {
    if (!user) return;

    try {
      await markAsSold(user.uid, verdictId, soldPrice, soldNote, soldDate, soldBuyer, reconciliationStatus, linkedTransactionId);

      // Auto-update highest sale if this sale exceeds it
      const currentHighest = profile?.highestSale ?? 0;
      if (soldPrice > currentHighest) {
        updateProfile?.({
          highestSale: soldPrice,
          highestSalePrecision: 'exact' as const,
          highestSaleDate: soldDate || new Date().toISOString(),
          highestSaleSource: 'verdict' as const,
        }).catch(err => console.error('[useVerdicts] Failed to update highest sale:', err));
      }

      // Optimistically update local state
      setVerdicts(prev => prev.map(v =>
        v.id === verdictId
          ? { ...v, sold: true, soldPrice, soldDate: soldDate || new Date().toISOString(), soldNote, soldBuyer, reconciliationStatus: reconciliationStatus || 'reported', linkedTransactionId: linkedTransactionId || null, workflowStage: 'sold' as const, updatedAt: new Date().toISOString() }
          : v
      ));

      // Bump version so other hook instances refetch
      const currentVersion = parseInt(sessionStorage.getItem('verdictsVersion') || '0', 10);
      sessionStorage.setItem('verdictsVersion', String(currentVersion + 1));
    } catch (err: any) {
      console.error('Error marking verdict sold:', err);
      setError(err.message || 'Failed to update verdict');
      throw err;
    }
  }, [user]);

  // Undo sold status
  const unmarkVerdictSold = useCallback(async (verdictId: string) => {
    if (!user) return;

    try {
      await unmarkSold(user.uid, verdictId);

      // Optimistically update local state
      setVerdicts(prev => prev.map(v =>
        v.id === verdictId
          ? { ...v, sold: false, soldPrice: null, soldDate: null, soldNote: undefined, reconciliationStatus: 'none' as const, linkedTransactionId: null, workflowStage: 'audit' as const, updatedAt: new Date().toISOString() }
          : v
      ));

      const currentVersion = parseInt(sessionStorage.getItem('verdictsVersion') || '0', 10);
      sessionStorage.setItem('verdictsVersion', String(currentVersion + 1));
    } catch (err: any) {
      console.error('Error unmarking verdict sold:', err);
      setError(err.message || 'Failed to update verdict');
      throw err;
    }
  }, [user]);
  
  // Get verdict by ID from local cache
  const getVerdictById = useCallback((verdictId: string) => {
    return verdicts.find(v => v.id === verdictId);
  }, [verdicts]);
  
  // Compute summaries
  const summaries = verdicts.map(toVerdictSummary);
  
  return {
    verdicts,
    summaries,
    isLoading,
    isSaving,
    error,
    saveVerdict,
    updateVerdict: updateVerdictCallback,
    loadVerdicts,
    removeVerdict,
    markVerdictSold,
    unmarkVerdictSold,
    getVerdictById,
  };
}

export default useVerdicts;
